import React, { ReactElement, useEffect } from 'react';

import Spinner from 'components/general/loaders/Spinner/Spinner';

import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { useRedirectToBrowseInspectionTemplates } from 'hooks/useRedirect';

import {
  emptyDefaultValues,
  permissionsRedirectWithMessage,
  toDefaultValues,
} from './helpers';
import { deepCopy } from 'shared/domain/template/templateWizardDraft';
import {
  TemplateDefaultValues,
  TemplateWizardPopulatorProps,
} from './types';

import { MemoTemplateWizardForm } from '../form';
import { useInspectionTemplate } from 'store/inspection';
import { InspectionTemplateStatus } from 'shared/domain/template/templateStatus';

function TemplateWizardPopulator(
  props: TemplateWizardPopulatorProps
): ReactElement {
  const id = useAppUrlParams(UrlParamName.INSPECTION_TEMPLATE_ID);
  const isCreationMode = !id;
  const redirect = useRedirectToBrowseInspectionTemplates();
  const {
    inspectionTemplate,
    loading,
    inspectionTemplateDocuments: documents,
    openInspectionTemplate,
  } = useInspectionTemplate();

  const { submitCreation, submitEdition, publish } = props;

  const [defaultValues, setDefaultValues] =
    React.useState<TemplateDefaultValues>(emptyDefaultValues());
  const [canRender, setCanRender] = React.useState<boolean>(false);

  useEffect(() => {
    if (isCreationMode) {
      // Reset templateId in store to prevent loading documents from active template
      openInspectionTemplate(undefined);
      setCanRender(true);
      return;
    }
    if (!id) {
      return;
    }
    if (!loading && inspectionTemplate) {
      // Ignore stale store row until the fetched template matches the template id from the URL
      if (inspectionTemplate._id !== id) {
        return;
      }
      if (inspectionTemplate.status === InspectionTemplateStatus.published) {
        return permissionsRedirectWithMessage(
          redirect,
          'inspection_template_edit_disabled_reason'
        );
      }

      if (inspectionTemplate.deleted) {
        return permissionsRedirectWithMessage(
          redirect,
          'inspection_template_edit_on_archived_warning'
        );
      }

      setDefaultValues(toDefaultValues(inspectionTemplate));
      setCanRender(true);
    }
  }, [id, inspectionTemplate, loading, isCreationMode, redirect, openInspectionTemplate]);

  return canRender ? (
    <MemoTemplateWizardForm
      originalDefaultValues={deepCopy(defaultValues)}
      defaultValues={deepCopy(defaultValues)}
      submitHandler={id ? submitEdition : submitCreation}
      publishHandler={publish}
      generalNormDocuments={
        isCreationMode ? [] : documents.templateDocuments
      }
      idParam={id}
    />
  ) : (
    <Spinner reason='TemplateWizardPopulator canRender false' />
  );
}

export const MemoTemplateWizardPopulator = React.memo(
  TemplateWizardPopulator
);
